import { useEffect, useState, useCallback, useRef } from "react";
import "./AddProperty.css";
import { useNavigate } from "react-router";
import {
  DocumentEntityTypesEnum,
  DocumentService,
  DocumentTypesEnum,
  PropertyService,
  UnitService,
  UnitTypesEnum,
} from "@propertelligent/client-api";
import { useForm, FormProvider, Controller } from "react-hook-form";

import Card from "../card";
import InputField from "../form-fields/InputField";
import CounterField from "../form-fields/CounterField";
import Uploader from "../CustomUploader/Uploader";
import {
  JPEG_PNG_EXTENSIONS,
  JPEG_PNG_MIME_TYPES,
} from "../CustomUploader/imageUploadConstants";
import Radio from "../form-fields/radio";
import AddressAutocompleteRHF from "../AutoComplete/AddressAutocompleteRHF";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import apiHelper from "../../utils/apiHelper";
import FixedFooter from "../listings/add-listing-footer/fixed-footer";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import PropertyCardItem from "./PropertyCardItem";
import EmptyState from "../emptystate";
import AddUnitForm from "../unit/AddUnitForm";
import SimpleButton from "../buttons/Simple-button";
import UnitCardItem from "../unit/UnitCardItem";
import H2 from "../typography/H2";
import toastPropertyIcon from "../../assets/toastPropertyIcon.svg";
import RadioGroup from "../form-fields/RadioGroup";
import LeftArrowTopHeader from "../form-fields/LeftArrowTopHeader";
import { GetImages } from "../../utils/GetImages";

interface UnitFormValues {
  propertyType: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  unitImage: File | null;
}

interface SingleFamilyUnitFormValues {
  numberOfBedrooms: number;
  numberOfBathrooms: number;
  size: number;
  unitNumber: string;
}

const AddProperty = ({ ptyTypeId, setPtyTypeId, navigateto, fetchData }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isUnsavedChangesOpen, setIsUnsavedChangesOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("create-property");
  const [listingData, setListingData] = useState(null);
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [singleUnitAdd, setSingleUnitAdd] = useState(false);
  // Sync lock for Done double-click before React re-renders unitsLoading (sheet row 64).
  const unitSubmitInFlightRef = useRef(false);

  const methods = useForm<UnitFormValues>({
    defaultValues: {
      propertyType: "2",
      address: "",
      city: "",
      state: "",
      zip: "",
      unitImage: null,
    },
  });

  const singleFamilyUnitMethods = useForm<SingleFamilyUnitFormValues>({
    defaultValues: {
      numberOfBedrooms: 1,
      numberOfBathrooms: 1,
      size: 0,
      unitNumber: "",
    },
  });

  const {
    formState: { isDirty },
  } = methods;

  const { handleSubmit, control, watch, getValues } = methods;
  const { handleSubmit: handleSingleFamilyUnitSubmit } =
    singleFamilyUnitMethods;
  const propertyType = watch("propertyType");
  const cardClass = activeTab === "add-details" ? "no-bg mx-0 px-0 add-property-details" : "";
  const footerBtnLabel =
    activeTab === "add-details"
      ? "Done"
      : listingData?.id
        ? "Next"
        : "Create Property";

  const handleCancelAction = () => {
    setIsUnsavedChangesOpen(false);
  };

  const handleContinueWithoutSaving = () => {
    setIsUnsavedChangesOpen(false);
    navigate("/properties");
  };

  const fetchUnitList = useCallback(async (propertyId: number) => {
    try {
      setUnitsLoading(true);
      const response = await UnitService.getApiUnitPropertylist(propertyId);
      setUnits(response.success || []);
    } catch (error) {
      console.error("Failed to fetch units:", error);
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Unable to fetch units",
        type: NotificationType.Failure,
        title: "Property Cannot Be Added",
      });
    } finally {
      setUnitsLoading(false);
    }
  }, []);

  // The property was already created earlier this session -- must not re-POST a create
  // with the same address (the backend's duplicate-address validator rejects it as
  // "already exists"), just carry the user forward. Also resyncs propertyType to the
  // real created property: Add Details' own branching reads propertyType from the form,
  // so a radio flip on this tab that gets silently discarded here (any edit made after
  // creation -- address, photo, property type -- is intentionally not re-submitted; the
  // reported bug only asked for the existing selection to keep showing, not a
  // re-edit-and-resave flow) must not leave that stale value behind. Shared by both
  // onSubmit and handleFooterSubmit below, which are two independently reachable entry
  // points (a programmatic/native form submit vs. the footer button click) -- keeping
  // this in one place is what stops the two from drifting out of sync with each other.
  const advanceIfAlreadyCreated = () => {
    if (!listingData?.id) return false;
    if (listingData.propertyTypeId) {
      methods.setValue("propertyType", String(listingData.propertyTypeId));
    }
    setActiveTab("add-details");
    return true;
  };

  const onSubmit = async (data: UnitFormValues) => {
    // Defense-in-depth backstop for the programmatic/native form-submit path --
    // handleFooterSubmit already short-circuits the button click ahead of RHF
    // validation, but a direct form submit calls this instead.
    if (advanceIfAlreadyCreated()) return;

    try {
      setLoading(true);

      const propertyObject = {
        address: {
          streetAddress: data.address,
          city: data.city,
          state: data.state,
          zip: data.zip,
        },
        propertyTypeId: Number.parseInt(data.propertyType),
      };

      const response = await apiHelper(
        PropertyService.postApiProperty,
        {
          successMessage: (res) =>
            `${res?.success?.streetAddress} Created successfully` ||
            "Property Created Successfully",
          failureMessage: "Failed to create property",
          showNotification: true,
          title: "Property Created",
          successIcon: (
            <img src={toastPropertyIcon} alt="success" width="20" height="20" />
          ),
        },
        propertyObject,
      );

      if (response?.data?.success) {
        const createdProperty = response.data.success;
        const propertyId = Number(createdProperty.id);

        if (data.unitImage) {
          const imgData = {
            EntityTypeId: DocumentEntityTypesEnum.Property,
            EntityId: propertyId,
            DocumentTypeId: DocumentTypesEnum.BeforeImage,
            IsMain: true,
            Document: data.unitImage,
            FileName: data.unitImage.name,
          };

          await apiHelper(
            DocumentService.postApiDocument,
            {
              successMessage: "Property Created Successfully",
              showNotification: true,
            },
            imgData,
          );
        }

        // Create response has no mainImageBase64 (image is uploaded after create).
        // Refetch so Add Details / PropertyCardItem can show the uploaded photo.
        let listing = createdProperty;
        if (data.unitImage) {
          const refreshed = await apiHelper(
            PropertyService.getApiProperty1,
            { showNotification: false },
            propertyId,
          );
          if (refreshed?.data?.success) {
            listing = refreshed.data.success;
          }
        }

        setListingData(listing);
        setActiveTab("add-details");
        fetchData();
      }
    } catch (error) {
      console.error("Submission failed", error);
    } finally {
      setLoading(false);
    }
  };

  const onSingleFamilyUnitSubmit = async (data: SingleFamilyUnitFormValues) => {
    // Guard re-entry: FixedFooter can fire twice before the first POST settles
    // (Done used to ignore unitsLoading), which caused a duplicate Unit create and
    // the "Unit number already exists in this property" toast (sheet row 64).
    if (
      unitSubmitInFlightRef.current ||
      unitsLoading ||
      singleUnitAdd ||
      units.length > 0
    ) {
      return;
    }

    unitSubmitInFlightRef.current = true;
    try {
      setUnitsLoading(true);
      const unitObject = {
        propertyId: Number(listingData.id),
        unitTypeId: UnitTypesEnum.CondoApartment,
        unitNumber: data.unitNumber.trim() || "Main House",
        numberOfBedrooms: Number(data.numberOfBedrooms),
        numberOfBathrooms: Number(data.numberOfBathrooms),
        size: Number(data.size),
      };

      const response = await apiHelper(
        UnitService.postApiUnit,
        {
          title: "Unit Added",
          successMessage: "Unit added successfully",
          showNotification: true,
          successIcon: (
            <img
              src={GetImages.toastUnitIcon}
              alt="success"
              width="20"
              height="20"
            />
          ),
        },
        unitObject,
      );

      if (response?.data?.success) {
        setSingleUnitAdd(true);
        await fetchUnitList(Number(listingData.id));
        singleFamilyUnitMethods.reset();
      }
    } catch (error) {
      console.error("Failed to add unit:", error);
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Unable to add unit",
        type: NotificationType.Failure,
        title: "Property Added",
      });
    } finally {
      unitSubmitInFlightRef.current = false;
      setUnitsLoading(false);
    }
  };

  const handleMultipleFamilyUnitAdded = useCallback(() => {
    if (listingData?.id) {
      fetchUnitList(Number(listingData.id));
    }
  }, [listingData, fetchUnitList]);

  const handleTabClick = (tabId: string) => {
    if (tabId === "add-details" && !listingData) return;
    setActiveTab(tabId);
  };

  const handleFooterSubmit = () => {
    if (loading || unitsLoading || unitSubmitInFlightRef.current) {
      return;
    }

    if (activeTab === "add-details") {
      if (getValues("propertyType") === "2") {
        navigate("/properties");
        return;
      }
      // After a successful add (or if the unit list already shows one), Done must
      // navigate — never re-POST the same unit number (sheet row 64).
      if (singleUnitAdd || units.length > 0) {
        navigate("/properties");
        return;
      }
      handleSingleFamilyUnitSubmit(onSingleFamilyUnitSubmit)();
      return;
    }

    // Checked here (ahead of RHF validation) so returning to this tab with nothing left
    // to change, or with a field that would now fail validation, still lets the user
    // move forward instead of hitting a dead end.
    if (advanceIfAlreadyCreated()) return;

    handleSubmit(onSubmit)();
  };

  useEffect(() => {
    if (activeTab === "add-details" && listingData?.id) {
      fetchUnitList(Number(listingData.id));
    }
  }, [activeTab, listingData, fetchUnitList]);

  // Add Details' own branching (handleFooterSubmit above) reads propertyType from the
  // form, not from listingData -- once the property exists, keep the form's value
  // pinned to what was actually created so navigating back to tab 1 and flipping the
  // radio (discarded on re-submit, see the guard above) can't desync the two tabs and
  // route "Done" into the wrong branch for a property that no longer matches.
  useEffect(() => {
    if (listingData?.propertyTypeId) {
      methods.setValue("propertyType", String(listingData.propertyTypeId));
    }
  }, [listingData, methods]);

  useEffect(() => {
    setPtyTypeId();
  }, [setPtyTypeId]);

  return (
    <>
      <div className="d-flex flex-column gap-1 full-page-content create-property-page">
        {/* Tab Navigation */}
        <div className="add-listing-tabs">
          <div className="">
            <nav className="nav nav-tabs justify-content-between horizontal-tabs">
              {/* Tab 1: Create Property */}
              <button
                className={`nav-link  px-0 py-3 fw-600 bg-transparent ${
                  activeTab === "create-property"
                    ? "active" // Active state
                    : "completed " // Completed state (when user is on tab 2)
                }`}
                onClick={() => handleTabClick("create-property")}
                type="button"
                style={{ width: "50%", textAlign: "start" }}
              >
                Create Property
              </button>

              {/* Tab 2: Add Details */}
              <button
                className={`nav-link  px-0 py-3 fw-600 bg-transparent  ${
                  activeTab === "add-details"
                    ? "active" // Active state
                    : "not-started text-muted" // Not started state (when user is on tab 1)
                }`}
                onClick={() => handleTabClick("add-details")}
                type="button"
                disabled={!listingData}
                style={{
                  cursor: listingData ? "pointer" : "not-allowed",
                  width: "50%",
                  textAlign: "start",
                }}
              >
                Add Details
              </button>
            </nav>
          </div>
          <LeftArrowTopHeader
            className="mx-0 mt-1"
            title={
              activeTab === "create-property"
                ? "Create Property"
                : "Add Details"
            }
            backPath="/properties"
          />
        </div>

        {/* Tab Content */}
        <Card className={`${cardClass} mt-0`}>
          {activeTab === "create-property" && (
            <div className="row">
              <div className="col-md-6">
                <div className="add-property-form-inner">
                  <FormProvider {...methods}>
                    <form id="unitForm" onSubmit={handleSubmit(onSubmit)}>
                      <div className="row">
                        <div className="col-md-12  d-flex align-items-center gap-3">
                          <RadioGroup
                            label="Property Type"
                            required
                            requiredMessage="Please select property type"
                          >
                            <Radio
                              name="propertyType"
                              value="1"
                              label="Single Family"
                            />
                            <Radio
                              name="propertyType"
                              value="2"
                              label="Multi Family"
                            />
                          </RadioGroup>
                        </div>

                        <div className="col-md-12">
                          <Controller
                            name="unitImage"
                            control={control}
                            render={({ field: { onChange } }) => (
                              <Uploader
                                name="unitImage"
                                // The whole create-property tab (including this Uploader) is
                                // conditionally rendered on activeTab, so it fully unmounts/
                                // remounts when navigating to Add Details and back -- without
                                // defaultImage its local preview state starts blank on every
                                // remount even though the property (and its image) already
                                // exists. Same pattern as EditProperty.tsx's mainImage prop.
                                defaultImage={
                                  listingData?.mainImageBase64
                                    ? "data:image/*;base64," + listingData.mainImageBase64
                                    : undefined
                                }
                                allowedFormats={[...JPEG_PNG_MIME_TYPES]}
                                allowedExtensions={[...JPEG_PNG_EXTENSIONS]}
                                maxSizeMB={2}
                              />
                            )}
                          />
                        </div>

                        <div className="col-md-12">
                          <AddressAutocompleteRHF />
                        </div>
                        <div className="col-md-12">
                          <InputField
                            name="address"
                            label="Street Address"
                            required
                            placeholder="Enter Address"
                            requiredMessage="Street Address is required"
                            validation={{
                              maxLength: {
                                value: 100,
                                message:
                                  "Street Address cannot exceed 100 characters",
                              },
                            }}
                          />
                        </div>

                        <div className="col-md-4">
                          <InputField
                            name="city"
                            label="City"
                            required
                            placeholder="Enter City"
                            requiredMessage="City is required"
                            validation={{
                              maxLength: {
                                value: 70,
                                message: "City cannot exceed 70 characters",
                              },
                              pattern: {
                                value: /^[A-Za-z\s]+$/,
                                message: "City must contain only letters",
                              },
                            }}
                          />
                        </div>

                        <div className="col-md-4">
                          <InputField
                            name="state"
                            label="State"
                            required
                            placeholder="Enter State"
                            requiredMessage="State is required"
                            uppercase
                            validation={{
                              pattern: {
                                value: /^[A-Z]{2}$/,
                                message: "Please enter the state as its 2-letter code",
                              },
                            }}
                          />
                        </div>

                        <div className="col-md-4">
                          <InputField
                            name="zip"
                            label="Zip Code"
                            required
                            requiredMessage="Zip Code is required"
                            placeholder="Enter Zip Code"
                            validation={{
                              pattern: {
                                value: /^\d{5}$/,
                                message: "Zip code must be 5 digits",
                              },
                            }}
                          />
                        </div>
                      </div>
                    </form>
                  </FormProvider>
                </div>
              </div>
            </div>
          )}

          {activeTab === "add-details" && listingData && (
            <div className={`${cardClass} row`}>
              <div className="col-md-12 mt-0">
                <div className="py-0">
                  <PropertyCardItem
                    key={listingData.id}
                    listingData={listingData}
                    IsFooter={false}
                  />

                  {unitsLoading ? (
                    <p>Loading units...</p>
                  ) : units.length > 0 ? (
                    <Card className="p-3 mx-1">
                      <div className="d-flex justify-content-between mb-4">
                        <H2
                          text={
                            units.length > 0 ? `${units.length} Units` : "Units"
                          }
                        />
                        {propertyType !== "1" && (
                          <SimpleButton
                            className="text-end"
                            label={"Add Unit"}
                            onClick={() => setModalOpen(true)}
                          />
                        )}
                      </div>
                      <div className="row">
                        {units.map((unit) => (
                          <div key={unit.id} className="col-md-12 mb-3">
                            <UnitCardItem listingData={unit} IsFooter={false} />
                          </div>
                        ))}
                      </div>
                    </Card>
                  ) : propertyType === "1" ? (
                    <Card className="p-3 mx-0">
                      <FormProvider {...singleFamilyUnitMethods}>
                        <form
                          onSubmit={handleSingleFamilyUnitSubmit(
                            onSingleFamilyUnitSubmit,
                          )}
                        >
                          <div className="row">
                            <div className="col-12 col-md-6 col-lg-4">
                              <div className="d-flex justify-content-between">
                                <CounterField
                                  name="numberOfBedrooms"
                                  label="Bedrooms"
                                  required
                                  requiredMessage="Number of bedrooms is required"
                                  min={1}
                                  max={10}
                                />
                                <CounterField
                                  name="numberOfBathrooms"
                                  label="Bathrooms"
                                  required
                                  requiredMessage="Number of bathrooms is required"
                                  min={1}
                                  max={10}
                                />
                              </div>
                              <InputField
                                name="unitNumber"
                                label="Unit Number"
                                required
                                requiredMessage="Unit number is required"
                                className="mt-2"
                                type="text"
                                placeholder="Enter Unit Number"
                                validation={{
                                  minLength: {
                                    value: 1,
                                    message: "Unit number is required",
                                  },
                                }}
                              />

                              <InputField
                                name="size"
                                label="Area Sqft"
                                required
                                requiredMessage="Size is required"
                                className="mt-3"
                                type="number"
                                placeholder="Enter Sqft"
                                validation={{
                                  min: {
                                    value: 1,
                                    message: "Size must be at least 1",
                                  },
                                  max: {
                                    value: 100000,
                                    message: "Size must be less than 100,000",
                                  },
                                }}
                              />
                            </div>

                            <div className="col-md-9"></div>
                          </div>
                        </form>
                      </FormProvider>
                    </Card>
                  ) : (
                    <EmptyState
                      className="mt-3"
                      icon={GetImages.emptyStateUnit}
                      heading="Add Units To Your Property"
                      buttonLabel="Add Unit"
                      onButtonClick={() => setModalOpen(true)}
                      bodytext={
                        <>
                          Want to add units later?{" "}
                          <a className="body-link" href="/properties">
                            Skip for now
                          </a>
                        </>
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>

        <FixedFooter
          onCancel={() =>
            isDirty ? setIsUnsavedChangesOpen(true) : navigate("/properties")
          }
          onSubmit={handleFooterSubmit}
          loading={loading || unitsLoading}
          buttonLabel={footerBtnLabel}
        />
      </div>

      {isModalOpen && (
        <AddUnitForm
          isModalOpen={isModalOpen}
          setModalOpen={setModalOpen}
          fetchData={fetchData}
          propertyId={listingData?.id}
          onUnitAdded={handleMultipleFamilyUnitAdded}
        />
      )}

      <ConfirmationPopup
        isOpen={isUnsavedChangesOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. If you go back or exit this step, your changes will be lost. Do you want to continue?"
        primaryButtonLabel="Cancel"
        secondaryButtonLabel="Continue Without Saving"
        onPrimaryAction={handleCancelAction}
        onSecondaryAction={handleContinueWithoutSaving}
        onClose={handleCancelAction}
        isPrimaryAction={false}
      />
    </>
  );
};

export default AddProperty;
